import type { SelectMultiChoicelistOption } from 'components/shared/select-multi-choicelist/SelectMultiChoicelist';
import { useMemo } from 'react';
import { MappingCountryMarkets } from './useMappingCountryMarkets';

export interface MarketRateOptions {
    options: SelectMultiChoicelistOption<number>[];
    /** Labels for the currently selected ids, including ones no longer offered by the market. */
    selectedOptions: SelectMultiChoicelistOption<number>[];
}

/**
 * Resolves a country's selected rate ids to labels.
 *
 * A rate saved earlier but since deleted from the market would otherwise render as a bare id, so the
 * name the GET mapping stored alongside it is the fallback.
 */
export const useMarketRateOptions = (
    country: MappingCountryMarkets,
    values: number[],
    options: SelectMultiChoicelistOption<number>[]
): MarketRateOptions => {
    const savedNames = useMemo(() => {
        const byId = new Map<number, string>();

        country.mapping.providerRates.forEach((rates) => {
            rates.shippingRates.forEach((rate) => {
                if (rate.name) byId.set(rate.id, rate.name);
            });
        });

        return byId;
    }, [country.mapping]);

    const selectedOptions = useMemo(() => {
        const labels = new Map(options.map((option) => [option.value, option.label]));

        return values.map((value) => ({
            value,
            label: labels.get(value) ?? savedNames.get(value) ?? `#${value}`,
        }));
    }, [values, options, savedNames]);

    return { options, selectedOptions };
};
